// Arlo HQ inside the Android app (android/: the site full screen through
// Chrome, plus home-screen widgets). The app opens the site with ?app=android,
// and with &link=1 until its widgets are linked; once someone is signed in
// here, the widgets are linked without anyone having to do anything.

import { useEffect, useState } from 'react';
import { api } from './api.ts';
import { useDevice } from './hooks.ts';

const IN_APP = 'arlo:android-app';
const APP_VERSION = 'arlo:android-app-version';
const AUTOLINK = 'arlo:widgets-autolink';
export const APP_APK = '/arlo-hq.apk';
const PACKAGE = 'hq.arlo.widgets';

const get = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
// The app shares Chrome's storage, so "in the app" is remembered per tab (it survives reloads there, not into Chrome).
const inTab = {
  get: () => {
    try {
      return sessionStorage.getItem(IN_APP) === '1';
    } catch {
      return false;
    }
  },
  set: () => {
    try {
      sessionStorage.setItem(IN_APP, '1');
    } catch {
      /* storage unavailable */
    }
  },
  version: (v?: string) => {
    try {
      if (v !== undefined) sessionStorage.setItem(APP_VERSION, v);
      return Number(sessionStorage.getItem(APP_VERSION) ?? '0') || 0;
    } catch {
      return 0;
    }
  },
};
const set = (k: string, v: string | null) => {
  try {
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    /* storage unavailable */
  }
};

/** Read (and tidy away) the app's hints in the address. Call once, before anything else reads the URL. */
export function noteAndroidApp(): void {
  const url = new URL(location.href);
  if (url.searchParams.get('app') !== 'android') return;
  inTab.set();
  // Versions before 2.2 didn't say: they count as 0, so they're offered the update.
  inTab.version(url.searchParams.get('appv') ?? '0');
  if (url.searchParams.get('link') === '1') set(AUTOLINK, '1');
  url.searchParams.delete('app');
  url.searchParams.delete('appv');
  url.searchParams.delete('link');
  history.replaceState(null, '', url.pathname + (url.search ? url.search : '') + url.hash);
}

export const inAndroidApp = (): boolean => inTab.get() || document.referrer.startsWith(`android-app://${PACKAGE}`);

/** Opens the app's linking step with a one-time code — or, if the app isn't installed, downloads it. */
export function widgetLinkUrl(code: string): string {
  const server = location.origin;
  const q = `server=${encodeURIComponent(server)}&code=${encodeURIComponent(code)}`;
  return `intent://link?${q}#Intent;scheme=arlohq;package=${PACKAGE};S.browser_fallback_url=${encodeURIComponent(server + APP_APK)};end`;
}

/** The Android app's version (0 if unknown or not in the app). */
export const androidAppVersion = (): number => (inAndroidApp() ? inTab.version() : 0);

/** From this version the app picks visitors from the phone's own contacts (arlohq://contacts). */
export const NATIVE_CONTACTS_VERSION = 8;
export const APK_URL = APP_APK;

/**
 * The app's native contact picker: it reads the name and photo of each person
 * picked and sends them to Arlo HQ itself, then steps back here. A one-time
 * code goes along in case the app isn't linked yet.
 */
export async function pickNativeContacts(name?: string): Promise<void> {
  const r = await api<{ code: string }>('/api/widgets/link', { body: { name } });
  const q = `server=${encodeURIComponent(location.origin)}&code=${encodeURIComponent(r.code)}`;
  // No app, or one too old to know arlohq://contacts: Chrome opens the fallback instead, which says to update.
  const fallback = `${location.origin}/?contacts=app-needed${location.hash || '#/'}`;
  location.href = `intent://contacts?${q}#Intent;scheme=arlohq;package=${PACKAGE};S.browser_fallback_url=${encodeURIComponent(fallback)};end`;
}

/** Came back from the fallback (the app missing or too old for contacts)? True once, and tidies the address. */
export function contactsAppNeeded(): boolean {
  const url = new URL(location.href);
  if (url.searchParams.get('contacts') !== 'app-needed') return false;
  url.searchParams.delete('contacts');
  history.replaceState(null, '', url.pathname + url.search + url.hash);
  return true;
}

/** Ask for a one-time code and hand it to the app. */
export async function linkWidgets(name?: string): Promise<void> {
  const r = await api<{ code: string }>('/api/widgets/link', { body: { name } });
  location.href = widgetLinkUrl(r.code);
}

/** In the app, with the widgets not yet linked: link them as soon as an owner is signed in. */
export function useWidgetAutoLink(): void {
  const d = useDevice();
  useEffect(() => {
    if (!d.key || d.guest || get(AUTOLINK) !== '1') return;
    // Once only, even if it fails: Settings → This device has a button for another go.
    set(AUTOLINK, null);
    void linkWidgets(d.account?.name).catch(() => undefined);
  }, [d.key, d.guest, d.account?.name]);
}

const PROMO = 'arlo:app-promo-dismissed';

/** An Android phone in a browser (not the app, not the kitchen tablet, not a sitter): worth mentioning the app. */
export function canGetApp(d: { kiosk?: boolean; guest?: unknown }): boolean {
  return /Android/i.test(navigator.userAgent) && !inAndroidApp() && !d.kiosk && !d.guest;
}

/** The "get the app" banner on Today: until it's dismissed on this phone. */
export function useAppPromo(): { show: boolean; dismiss: () => void } {
  const d = useDevice();
  const [dismissed, setDismissed] = useState(() => get(PROMO) !== null);
  return {
    show: canGetApp(d) && !dismissed,
    dismiss: () => {
      set(PROMO, String(Date.now()));
      setDismissed(true);
    },
  };
}

const UPDATE_DISMISSED = 'arlo:app-update-dismissed';

/** In the app, and a newer version is on the website: offer it (until dismissed for that version). */
export function useAppUpdate(): { latest?: string; dismiss: () => void } {
  const [latest, setLatest] = useState<{ code: number; name: string } | undefined>();
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (!inAndroidApp()) return;
    // Opened some other way than the app's icon (a share, a link): it didn't say which version it is, so don't guess "old".
    if (!inTab.get()) return;
    fetch('/arlo-hq-version.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : undefined))
      .then((j?: { versionCode?: number; versionName?: string }) => {
        if (!j?.versionCode || j.versionCode <= inTab.version()) return;
        if (get(UPDATE_DISMISSED) === String(j.versionCode)) return;
        setLatest({ code: j.versionCode, name: j.versionName ?? String(j.versionCode) });
      })
      .catch(() => undefined);
  }, []);
  return {
    latest: latest && !hidden ? latest.name : undefined,
    dismiss: () => {
      if (latest) set(UPDATE_DISMISSED, String(latest.code));
      setHidden(true);
    },
  };
}
